import React, { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@jammable/ui-components/Forms";
import { FaTimes, FaPlus } from "react-icons/fa";

const CategoryPicker = ({ newCategories, setNewCategories }) => {
  const [badges, setBadges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch badges from API
  useEffect(() => {
    const fetchBadges = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`/api/model/getBadges`);
        if (response.data && response.data.data) {
          setBadges(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching badges:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBadges();
  }, []);

  const addCustomTag = () => {
    if (!searchTerm.trim()) return;

    const customTag = searchTerm.trim().toLowerCase();

    // Check if already selected
    if (newCategories.includes(customTag)) {
      setSearchTerm("");
      return;
    }

    // Check if we have room for another tag
    if (newCategories.length >= 3) return;

    setNewCategories([...newCategories, customTag]);
    setSearchTerm("");
  };

  // Check if we should show the custom tag option
  const shouldShowCustomTag =
    searchTerm.trim() &&
    !badges.some(
      (badge) => badge.badge.toLowerCase() === searchTerm.trim().toLowerCase(),
    ) &&
    newCategories.length < 3 &&
    !newCategories.includes(searchTerm.trim().toLowerCase());

  return (
    <div className="tw-mb-4 tw-w-full">
      <div className="tw-text-sm tw-font-semibold tw-mb-3 tw-flex tw-items-center tw-mt-2">
        <span className="tw-mr-2 tw-text-base">Tags</span>
        <span className="tw-bg-purple-100 tw-text-purple-700 tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-font-black">
          {newCategories.length}/3
        </span>
        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-2">
          {newCategories.map((category) => (
            <span
              onClick={() => {
                setNewCategories((prevCategories) =>
                  prevCategories.filter((c) => c !== category),
                );
              }}
              key={category}
              className="tw-bg-purple-100 tw-cursor-pointer tw-text-purple-700 tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-font-black tw-flex tw-items-center tw-gap-1"
            >
              {category}
              <FaTimes />
            </span>
          ))}
        </div>
      </div>

      {/* Search input */}
      <div className="tw-mb-3 tw-w-full">
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search tags..."
          className="tw-h-10 tw-w-full"
          onKeyDown={(e) => {
            if (e.key === "Enter" && shouldShowCustomTag) {
              e.preventDefault();
              addCustomTag();
            }
          }}
        />
      </div>

      <div className="tw-w-full tw-flex tw-flex-col tw-items-center">
        {isLoading ? (
          <div className="tw-w-full tw-flex tw-flex-wrap tw-gap-2">
            {/* Skeleton loaders for tags */}
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="tw-animate-pulse tw-bg-gray-200 tw-rounded-full tw-h-8 tw-w-24 tw-flex tw-items-center tw-px-3"
              ></div>
            ))}
          </div>
        ) : badges.length === 0 && !shouldShowCustomTag ? (
          <div className="tw-text-center tw-py-4 tw-text-gray-500 tw-w-full">
            No tags found. Try a different search term.
          </div>
        ) : (
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-w-full">
            {/* Display existing badges */}
            {badges
              .filter((badge) => badge.badge.includes(searchTerm))

              .slice(0, 9)
              .map((badge) => {
                const isSelected = newCategories?.includes(
                  badge.badge.toLowerCase(),
                );
                const isDisabled = !isSelected && newCategories.length >= 3;

                return (
                  <div
                    key={badge.badge}
                    onClick={() => {
                      if (isDisabled) return;

                      const categoryName = badge.badge?.trim().toLowerCase();

                      setNewCategories((prevCategories) => {
                        if (prevCategories.includes(categoryName)) {
                          // If the category name is already in the array, remove it
                          return prevCategories.filter(
                            (item) => item !== categoryName,
                          );
                        } else {
                          if (prevCategories.length < 3) {
                            // If there are less than 3 items, add the new category
                            return [...prevCategories, categoryName];
                          }
                          // If there are already 3 items, return the array unchanged
                          return prevCategories;
                        }
                      });
                    }}
                    className={`
                    tw-cursor-pointer 
                    tw-rounded-full 
                    tw-py-1 
                    tw-px-3 
                    tw-text-xs 
                    tw-font-semibold
                    tw-flex 
                    tw-items-center 
                    tw-transition-all 
                    tw-duration-200
                    ${
                      isSelected
                        ? "tw-bg-purple-600 tw-text-white tw-shadow-sm"
                        : "tw-bg-gray-100 tw-text-black tw-border tw-border-purple-200 hover:tw-border-purple-400 hover:tw-bg-purple-50"
                    }
                  `}
                  >
                    <span className="tw-mr-1.5 tw-text-base">
                      {badge.emoji}
                    </span>
                    <span className="">{badge.badge}</span>
                    <span className="tw-ml-1.5 tw-text-xs tw-opacity-70 tw-bg-black/10 tw-px-1.5 tw-py-0.5 tw-rounded-full">
                      {badge.count}
                    </span>
                  </div>
                );
              })}

            {/* Display custom tag option if needed */}
            {shouldShowCustomTag && (
              <div
                onClick={addCustomTag}
                className="tw-cursor-pointer tw-rounded-full tw-py-1 tw-px-3 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-transition-all tw-duration-200 tw-bg-purple-50 tw-text-purple-700 tw-border tw-border-purple-300 hover:tw-bg-purple-100 tw-gap-1"
              >
                <FaPlus size={14} />
                <span className="tw-flex-1">{searchTerm.trim()}</span>
                <span className="tw-text-xs tw-opacity-70 tw-bg-white tw-px-1.5 tw-py-0.5 tw-rounded-full tw-text-purple-700">
                  new
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPicker;
